import { Box, Button, Container, Grid, Skeleton, Typography } from '@mui/material';
import { Outlet, useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialog } from 'src/hooks/use-dialog';

import { ArrowLeft as ArrowLeftIcon } from '../../../../icons/arrow-left';
import { ExclamationOutlined as ExclamationOutlinedIcon } from '../../../../icons/exclamation-outlined';
import {
  ParkingTicketState,
  Visitor,
  VisitorNotificationMethod,
  VisitState,
} from '../../api/types/visits';
import { useVisit } from '../../hooks/query/use-visit';
import { useVisitActionDialog } from '../../hooks/use-visit-action-dialog';
import { VisitContactInfo } from '../molecules/visit-contact-info';
import { VisitDestination } from '../molecules/visit-destination';
import { VisitHost } from '../molecules/visit-host';
import { VisitHours } from '../molecules/visit-hours';
import { VisitStatus } from '../molecules/visit-status';
import { VisitTimeline } from '../molecules/visit-timeline';
import { ApplyDiscountForParkingTicketDialog } from '../organisms/dialog/apply-discount-for-parking-ticket-dialog/apply-discount-for-parking-ticket-dialog';

import { ActionsTableMenu } from '@/components/atoms/actions-table-menu';
import { Dialog } from '@/components/molecules/dialog';
import { VisitAttachments } from '@/features/visits/components/molecules/visit-attachments';
import { VisitFinishReason } from '@/features/visits/components/molecules/visit-finish-reason';
import { VisitReason } from '@/features/visits/components/molecules/visit-reason';
import { ExtendVisitDialog } from '@/features/visits/components/organisms/dialog/extend-visit-dialog';
import { useNavigateBack } from '@/hooks/use-navigate-back';
import { getMessage } from '@/utils/get-message';

import { usePermissions, withPermissions } from '@/permissions/can';
import { useResendVisitorInvitation } from '../../hooks/mutation/use-resend-visitor-invitation';

const Visit = () => {
  const { t } = useTranslation(['visit', 'visits', 'common']);
  const { navigateBack } = useNavigateBack();

  const permissions = usePermissions();

  const visitorId = useSearch({
    from: '/_layout/visits/$visitId',
    select: state => state.visitorId,
  });

  const [isOpenExtendVisitDialog, handleOpenExtendVisitDialog, handleCloseExtendVisitDialog] =
    useDialog();

  const [payVisitorId, setPayVisitorId] = useState<number | null>(null);

  const visitId = useParams({
    from: '/_layout/visits/$visitId',
    select: state => (state.visitId ? +state.visitId : 0),
  });

  const { data, isLoading, error } = useVisit(visitId);

  const {
    isOpen: isVisitActionDialogOpen,
    openConfirmDialog,
    openRejectDialog,
    closeDialog,
    dialogConfig: visitActionDialogConfig,
    isActionInProgress: isVisitActionInProgress,
  } = useVisitActionDialog();

  const { mutate: resendVisitorInvitation } = useResendVisitorInvitation();

  const payVisitor = data?.visitors.find(visitor => visitor.id === payVisitorId);
  const isPayDisabled =
    permissions.cannot('applyDiscount', 'Visitors') ||
    !payVisitor?.hasAccessToParking ||
    !payVisitor.discountTemplate ||
    payVisitor?.parkingTicket?.status === ParkingTicketState.DISCOUNTED;

  const actionsSlot = useCallback(
    (visitor: Visitor) => {
      const cannotApplyDiscount = permissions.cannot('applyDiscount', 'Visitors');
      const isTicketDiscounted = visitor?.parkingTicket?.status === ParkingTicketState.DISCOUNTED;
      const hasDiscount = visitor.discountTemplate !== null;

      const actions = [
        {
          key: 'resend-invitation',
          label: t('visit:resendInvitation.title'),
          onClick: () => {
            resendVisitorInvitation({
              visitorId: visitor.id,
              method: VisitorNotificationMethod.EMAIL,
            });
          },
          hidden: data?.state !== VisitState.CREATED && data?.state !== VisitState.STARTED,
        },
        {
          key: 'apply-discount',
          label: t('visit:modal.pay'),
          onClick: () => setPayVisitorId(visitor.id),
          disabled: cannotApplyDiscount || isTicketDiscounted || !hasDiscount,
          hidden: !visitor.hasAccessToParking,
          tooltip: getMessage([
            {
              check: cannotApplyDiscount,
              message: t('common:notSufficientPermissions'),
            },
            {
              check: isTicketDiscounted,
              message: t('visit:modal.alreadyDiscounted'),
            },
            {
              check: !hasDiscount,
              message: t('visit:modal.noDiscountAssigned'),
            },
          ]),
        },
      ];

      return <ActionsTableMenu actions={actions} />;
    },
    [data?.state, permissions, resendVisitorInvitation, t],
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ py: 4 }}>
          <Skeleton height={42} />
          <Skeleton />
          <Skeleton />
        </Box>
      );
    }

    if (error || !data) {
      return (
        <Box sx={{ py: 4 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              p: 3,
            }}>
            <ExclamationOutlinedIcon />
            <Typography color="textSecondary" sx={{ mt: 2 }} variant="body2">
              {error?.response?.data?.message}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ py: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              color="primary"
              startIcon={<ArrowLeftIcon />}
              onClick={() => navigateBack('/visits')}
              variant="text">
              {t('visit:back')}
            </Button>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              mb: 2,
              justifyContent: 'space-between',
            }}>
            <Typography color="textPrimary" variant="h4">
              # {data?.id}
            </Typography>

            {/* Only when false (not null) */}
            {data?.hostConfirmed === false && data?.state !== VisitState.REJECTED && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => openConfirmDialog({ visitId: +(data?.id ?? visitId) })}
                  disabled={isVisitActionInProgress}>
                  {t('common:button.confirm')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => openRejectDialog({ visitId: +(data?.id ?? visitId) })}
                  disabled={isVisitActionInProgress}>
                  {t('common:button.cancel')}
                </Button>
              </Box>
            )}
          </Box>
          <Grid sx={{ mb: 3 }} container spacing={3}>
            <Grid
              container
              item
              lg={12}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <VisitContactInfo visitors={data?.visitors || []} actionsSlot={actionsSlot} />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid
              container
              item
              lg={8}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <VisitReason
                  category={data?.category}
                  hostConfirmed={data?.hostConfirmed}
                  deviceConfirmed={data?.deviceConfirmed}
                  visitState={data?.state}
                />
              </Grid>
              <Grid item xs={12}>
                <VisitDestination
                  additionalDestinations={data?.additionalDestinations || []}
                  mainDestination={data?.mainDestination}
                />
              </Grid>
              <Grid item xs={12}>
                <VisitHost host={data?.host} kioskEnteredHostName={data?.kioskEnteredHostName} />
              </Grid>
              <Grid item xs={12}>
                <VisitHours
                  arrivedAt={data?.arrivedAt}
                  createdAt={data?.createdAt}
                  expectedAt={data?.expectedAt}
                  finishesAt={data?.finishesAt}
                  visitState={data?.state}
                  handleOpenExtendVisitDialog={handleOpenExtendVisitDialog}
                />
              </Grid>
            </Grid>
            <Grid
              container
              item
              lg={4}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <VisitStatus visitId={visitId} visitState={data?.state} />
              </Grid>
              {data.state !== 'cancelled' && visitorId !== undefined && (
                <Grid item xs={12}>
                  <VisitTimeline visitorId={visitorId} visitState={data?.state} />
                </Grid>
              )}
              {data?.visitFinishReason ? (
                <Grid item xs={12}>
                  <VisitFinishReason visitFinishReason={data?.visitFinishReason} />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <VisitAttachments visitId={visitId} />
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Outlet />
        <ApplyDiscountForParkingTicketDialog
          open={payVisitorId !== null}
          visitorId={payVisitorId}
          isPayDisabled={isPayDisabled}
          onCancel={() => setPayVisitorId(null)}
        />
        <ExtendVisitDialog
          open={isOpenExtendVisitDialog}
          visitId={+data?.id}
          visitFinishesAt={new Date(data?.finishesAt || new Date())}
          onCancel={handleCloseExtendVisitDialog}
        />
        <Dialog
          open={isVisitActionDialogOpen}
          onCancel={closeDialog}
          onConfirm={visitActionDialogConfig?.onConfirm}
          title={visitActionDialogConfig?.title ?? ''}
          variant={visitActionDialogConfig?.variant}
          confirmButtonText={visitActionDialogConfig?.confirmButtonText}
          cancelButtonText={t('common:button.close')}
          isConfirmButtonDisabled={!visitActionDialogConfig}
          isConfirmButtonSubmitting={visitActionDialogConfig?.isSubmitting}>
          <Typography sx={{ my: 2 }} variant="subtitle2">
            {visitActionDialogConfig?.description}
          </Typography>
        </Dialog>
      </>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        flexGrow: 1,
      }}>
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}>
        {renderContent()}
      </Container>
    </Box>
  );
};

export default withPermissions(permissions => permissions.cannot('read', 'Visitors'), Visit);
